/**
 * AutoLocateArea Mixin
 * Automatically locates the map to the selected area from Vuex store when the map is loaded.
 * Supports zooming to region boundaries using geometry data from backend.
 */
import { applyTransform } from 'ol/extent'
import { getTransform, fromLonLat } from 'ol/proj'
import ViewAnimationUtil from '../util/ViewAnimation'
import { mapState } from 'vuex'
import GeoJSON from 'ol/format/GeoJSON'
import { WguEventBus } from '../WguEventBus'

export default {
  computed: {
    ...mapState(['selectedArea'])
  },
  watch: {
    selectedArea: {
      handler (val) {
        if (val) {
          this.autoLocateToSelectedArea()
        }
      },
      deep: true
    }
  },
  mounted () {
    // Listen for manual trigger from menu clicks
    WguEventBus.$on('trigger-map-locate', this.handleMapLocateTrigger)
  },
  beforeDestroy () {
    WguEventBus.$off('trigger-map-locate', this.handleMapLocateTrigger)
  },
  methods: {
    /**
     * Handle manual map locate trigger from menu clicks
     */
    handleMapLocateTrigger () {
      this.$nextTick(() => {
        const mapRef = this.map || this.$map
        if (mapRef) {
          mapRef.updateSize()
        }
        this.autoLocateToSelectedArea()
      })
    },
    /**
     * Auto locate to the selected area if exists
     * Call this method after the map is ready
     */
    async autoLocateToSelectedArea () {
      // 理赔页（/claim）地图定位由授权边界层逐级下钻独占管理，
      // 跳过全局按 selectedArea 的自动定位，避免覆盖收敛层级定位。
      if (this.$route && this.$route.name === 'Claim') {
        return
      }
      const mapRef = this.map || this.$map
      if (!mapRef || !this.selectedArea) {
        return
      }

      const view = mapRef.getView()
      const mapProjection = view.getProjection()

      try {
        // Priority 1: Use bounding box if available
        const hasBbox = Array.isArray(this.selectedArea?.boundingbox) && this.selectedArea.boundingbox.length === 4
        const bbox4326 = hasBbox ? this.selectedArea.boundingbox.map(v => parseFloat(v)) : null

        if (hasBbox && bbox4326.every(Number.isFinite)) {
          // Result with bounding box in EPSG:4326, transform to map projection
          const extent = applyTransform(bbox4326.slice(), getTransform('EPSG:4326', mapProjection))
          ViewAnimationUtil.to(view, extent)
          return
        }

        // Priority 2: Try to fetch from GeoServer WFS
        const regionCode = this.selectedArea.place_id || this.selectedArea.town_code
        if (regionCode) {
          const success = await this.locateByGeoServerWFS(regionCode, view, mapProjection)
          if (success) {
            return
          }
        }

        // Priority 3: Try to fetch from backend API (fallback)
        if (regionCode) {
          const success = await this.locateByRegionGeometry(regionCode, view, mapProjection)
          if (success) {
            return
          }
        }

        // Priority 4: Fall back to center point
        const lon = this.selectedArea?.lon != null ? parseFloat(this.selectedArea.lon) : null
        const lat = this.selectedArea?.lat != null ? parseFloat(this.selectedArea.lat) : null

        if (Number.isFinite(lon) && Number.isFinite(lat)) {
          // No bbox in result: center on lon/lat from result and zoom in
          const coords = fromLonLat([lon, lat], mapProjection)
          ViewAnimationUtil.to(view, coords)
        }
      } catch (e) {
        console.error('Error while auto-locating to selected area:', e)
      }
    },

    /**
     * Locate map by fetching region geometry from GeoServer WFS
     * @param {string} regionCode - The region code
     * @param {ol.View} view - OpenLayers view
     * @param {ol.proj.Projection} mapProjection - Map projection
     * @returns {Promise<boolean>} True if successful, false otherwise
     */
    async locateByGeoServerWFS (regionCode, view, mapProjection) {
      try {
        // Construct WFS GetFeature request to GeoServer
        const wfsUrl = 'https://geo.kungsong.com/geoserver/ks/wfs'
        const params = new URLSearchParams({
          service: 'WFS',
          version: '1.1.0',
          request: 'GetFeature',
          typeName: 'ks:geo_district_code',
          outputFormat: 'application/json',
          featureId: `geo_district_code.${regionCode}`
        })

        const response = await fetch(`${wfsUrl}?${params.toString()}`)
        if (!response.ok) {
          console.warn('GeoServer WFS request failed:', response.status)
          return false
        }

        const geojsonData = await response.json()

        if (!geojsonData || !geojsonData.features || geojsonData.features.length === 0) {
          console.warn('No features returned from GeoServer for region:', regionCode)
          return false
        }

        // Parse GeoJSON and extract extent
        const format = new GeoJSON()
        const features = format.readFeatures(geojsonData, {
          dataProjection: 'EPSG:4326',
          featureProjection: mapProjection
        })

        if (features.length === 0) {
          return false
        }

        // Get extent from the first feature
        const extent = features[0].getGeometry().getExtent()

        // Fit view to extent with padding and animation
        view.fit(extent, {
          padding: [50, 50, 50, 50],
          duration: 1000,
          maxZoom: 16
        })

        return true
      } catch (error) {
        console.error('Error locating by GeoServer WFS:', error)
        return false
      }
    },

    /**
     * Locate map by fetching region geometry from backend
     * @param {string} regionCode - The region code
     * @param {ol.View} view - OpenLayers view
     * @param {ol.proj.Projection} mapProjection - Map projection
     * @returns {Promise<boolean>} True if successful, false otherwise
     */
    async locateByRegionGeometry (regionCode, view, mapProjection) {
      try {
        // Fetch region geometry from backend via Vuex action
        const geojsonData = await this.$store.dispatch('fetchRegionGeometry', regionCode)

        if (!geojsonData || !geojsonData.features || geojsonData.features.length === 0) {
          console.warn('No geometry data returned for region:', regionCode)
          return false
        }

        // Parse GeoJSON and extract extent
        const format = new GeoJSON()
        const features = format.readFeatures(geojsonData, {
          dataProjection: 'EPSG:4326',
          featureProjection: mapProjection
        })

        if (features.length === 0) {
          return false
        }

        // Get extent from the first feature (or combine all features)
        let extent
        if (features.length === 1) {
          extent = features[0].getGeometry().getExtent()
        } else {
          // Combine extents from all features
          extent = features[0].getGeometry().getExtent()
          for (let i = 1; i < features.length; i++) {
            const featureExtent = features[i].getGeometry().getExtent()
            extent = [
              Math.min(extent[0], featureExtent[0]),
              Math.min(extent[1], featureExtent[1]),
              Math.max(extent[2], featureExtent[2]),
              Math.max(extent[3], featureExtent[3])
            ]
          }
        }

        // Fit view to extent with padding and animation
        view.fit(extent, {
          padding: [50, 50, 50, 50], // top, right, bottom, left
          duration: 1000,
          maxZoom: 16
        })

        return true
      } catch (error) {
        console.error('Error locating by region geometry:', error)
        return false
      }
    }
  }
}
